<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { formatDate, invoiceStatusLabel } from './format';
import { formatPrice } from '@/garage/format';
import { api, type DepositOrder } from '@/net/api';
import { useAccount } from '@/state/account';

/**
 * Billing: every transaction, and its invoice as a PDF.
 *
 * **This is core's data, read directly.** `GET /api/invoices` and
 * `GET /api/invoices/:number/download` are the template's own user-facing invoice
 * routes and they already serve exactly what this pane needs — number, amounts,
 * tax, currency, date, status, and a `pdfmake`-rendered document. A game-side
 * endpoint in front of them would be a second reader of a table this repo does not
 * own, so there is not one.
 *
 * A row exists here because a purchase wrote an invoice, and the *same* call that
 * wrote it emailed the PDF — see `WalletService.billFor`. So this pane and the
 * "successful purchase" email cannot disagree about a figure; they are one record
 * shown twice.
 */
const account = useAccount();

/** Core payment orders (t-game-core payments) — status incl. refunds. */
const deposits = ref<DepositOrder[]>([]);

const DEPOSIT_STATUS: Record<string, string> = {
  PENDING: 'Pending', APPROVED: 'Paid', DECLINED: 'Declined', ERROR: 'Failed', EXPIRED: 'Expired',
  MISMATCH: 'Under review', PARTIALLY_REFUNDED: 'Partly refunded', REFUNDED: 'Refunded',
};

onMounted(() => {
  account.loadBilling().catch(() => {});
  api.deposits().then((rows) => { deposits.value = rows; }).catch(() => {});
});

const pages = computed(() =>
  Math.max(1, Math.ceil(account.invoiceTotal.value / 20)),
);

/**
 * A link, not a fetch.
 *
 * Letting the browser navigate to the endpoint is what makes the server's
 * `Content-Disposition: attachment` do its job. Pulling the bytes into a blob and
 * synthesising a download would be more code for a worse result, and `download`
 * links are exactly what a sandboxed viewer blocks.
 */
function pdfUrl(number: string): string {
  return api.invoicePdfUrl(number);
}
</script>

<template>
  <div class="pane" data-test="pane-billing">
    <section class="block">
      <h3>Transactions</h3>
      <p class="hint">
        Every invoice raised on this account. The PDF is the same document that was
        emailed to you at the time.
      </p>

      <div v-if="account.invoices.value.length > 0" class="table-wrap">
        <table data-test="billing-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Description</th>
              <th class="num">Amount</th>
              <th>Status</th>
              <th><span class="sr">PDF</span></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in account.invoices.value"
              :key="invoice.id"
              :data-invoice="invoice.number"
            >
              <td class="mono">{{ invoice.number }}</td>
              <td>{{ formatDate(invoice.createdAt) }}</td>
              <td>{{ invoice.description }}</td>
              <td class="num">{{ formatPrice(invoice.totalCents, invoice.currency) }}</td>
              <td>
                <span
                  class="badge"
                  :class="invoice.status.toUpperCase() === 'PAID' ? 'ok' : 'neutral'"
                >
                  {{ invoiceStatusLabel(invoice.status) }}
                </span>
              </td>
              <td>
                <a
                  class="pdf"
                  :href="pdfUrl(invoice.number)"
                  target="_blank"
                  rel="noopener"
                  :data-test="`invoice-pdf-${invoice.number}`"
                  :title="`Download ${invoice.number} as a PDF`"
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path
                      d="M3.5 1.5h6l3 3v10h-9z"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.2"
                    />
                    <path d="M9.5 1.5v3h3" fill="none" stroke="currentColor" stroke-width="1.2" />
                    <path
                      d="M8 6.8v4.4M6.2 9.4 8 11.2l1.8-1.8"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <span class="sr">Download PDF</span>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else-if="account.loaded.value.billing" class="empty" data-test="billing-empty">
        No transactions yet. Anything bought with real money appears here with its
        invoice.
      </p>
      <p v-else class="empty">Loading…</p>

      <div v-if="pages > 1" class="pager">
        <button
          type="button"
          class="ghost"
          :disabled="account.invoicePage.value <= 1 || account.busy.value"
          @click="account.loadBilling(account.invoicePage.value - 1)"
        >
          Newer
        </button>
        <span>Page {{ account.invoicePage.value }} of {{ pages }}</span>
        <button
          type="button"
          class="ghost"
          :disabled="account.invoicePage.value >= pages || account.busy.value"
          @click="account.loadBilling(account.invoicePage.value + 1)"
        >
          Older
        </button>
      </div>
    </section>

    <section v-if="deposits.length > 0" class="block" data-test="billing-payments">
      <h3>Payments</h3>
      <p class="hint">Diamond purchases and their state, refunds included.</p>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Date</th>
              <th>Method</th>
              <th class="num">Amount</th>
              <th>Status</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in deposits" :key="d.orderId" :data-order="d.orderId">
              <td class="mono">{{ d.orderId }}</td>
              <td>{{ formatDate(d.createdAt) }}</td>
              <td>{{ d.method }}</td>
              <td class="num">
                {{ formatPrice(d.amountCents, d.currency) }}
                <small v-if="d.refundedCents > 0"> (−{{ formatPrice(d.refundedCents, d.currency) }})</small>
              </td>
              <td><span class="badge">{{ DEPOSIT_STATUS[d.status] ?? d.status }}</span></td>
              <td>
                <a v-if="d.invoiceNumber" class="mono" :href="pdfUrl(d.invoiceNumber)" target="_blank" rel="noopener">{{ d.invoiceNumber }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style scoped>
@import './pane.css';

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  white-space: nowrap;
}

table a {
  color: var(--accent);
  text-decoration: none;
}

table a:hover {
  text-decoration: underline;
}

.badge.neutral {
  background: rgba(99, 116, 138, 0.18);
  color: var(--steel-400);
}

.pdf {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 44px is the minimum tap target the project standardises on; this pane is the
     one place in the profile screen with an icon-only control, so unlike every
     other control here it takes the floor unconditionally rather than only on a
     coarse pointer — there is no label beside it to enlarge the hit area. */
  width: 2.75rem;
  height: 2.75rem;
  /* `SettingsPanel`'s `.win-close`: an icon button in this game is transparent
     with a border that is *there but invisible*, and hovering colours it in. A
     border reserved rather than added is what keeps the row from shifting, and
     it is the same discipline the rail chips follow one control along. */
  border: 1px solid transparent;
  border-radius: 3px;
  color: #7d8ca1;
  transition: color 120ms ease, border-color 120ms ease;
}

.pdf:hover,
.pdf:focus-visible {
  border-color: var(--steel-600);
  color: var(--accent);
}

.pdf svg {
  width: 1.05rem;
  height: 1.05rem;
}

@media (prefers-reduced-motion: reduce) {
  .pdf {
    transition: none;
  }
}

.pager .ghost {
  margin-top: 0;
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
